import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["children", "additionalContent", "className", "coordinates", "onClickOutside", "onChangeDirection", "anchorRef", "invert", "originalDirection", "closureModes", "closureOptions", "onClose", "container"],
  _excluded2 = ["isOpen"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import React, { forwardRef, useCallback, useRef, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { cnBem, combineRefs } from '@carnica/utils';
import { withDataConverter } from '@beef/react';
import { useComponentSize } from '@carnica/utils/hooks';
import { Popover } from "../../modules/Popover";
import { Text } from "../Text";
import styles from "./styles.pcss";
import { convertPopoverDirection, dataConverter, getOffset, variants } from "./utils";
import { TooltipArrow } from "./TooltipArrow";
import { useCloseOnScroll } from "./hooks/useCloseOnScroll";
import { useCloseWhenOutsideViewport } from "./hooks/useCloseWhenOutsideViewport";
var cnTooltip = cnBem('tooltip', styles);
var getClassName = function getClassName(invert, className) {
  return function (dir) {
    var direction = convertPopoverDirection(dir);
    return cnTooltip({
      direction: direction,
      invert: invert
    }, [typeof className === 'function' ? className(convertPopoverDirection(direction)) : className]);
  };
};
var defaultClosureModes = {
  outsideClick: true,
  scroll: false,
  viewport: false
};
var Content = /*#__PURE__*/forwardRef(function (props, ref) {
  var children = props.children,
    additionalContent = props.additionalContent,
    className = props.className,
    coordinates = props.coordinates,
    onClickOutside = props.onClickOutside,
    onChangeDirectionProp = props.onChangeDirection,
    anchorRef = props.anchorRef,
    invert = props.invert,
    originalDirection = props.originalDirection,
    closureModesProp = props.closureModes,
    closureOptions = props.closureOptions,
    onClose = props.onClose,
    container = props.container,
    otherProps = _objectWithoutProperties(props, _excluded);
  var closureModes = _objectSpread(_objectSpread({}, defaultClosureModes), closureModesProp || {});
  var tooltipRef = useRef(null);
  var _useState = useState(otherProps.direction),
    _useState2 = _slicedToArray(_useState, 2),
    direction = _useState2[0],
    setDirection = _useState2[1];
  var anchorSize = useComponentSize(anchorRef || {
    current: null
  });
  var onChangeDirection = function onChangeDirection(dir) {
    onChangeDirectionProp === null || onChangeDirectionProp === void 0 ? void 0 : onChangeDirectionProp(dir);
    setDirection(dir);
  };
  var handleClickOutside = useCallback(function (e) {
    onClickOutside === null || onClickOutside === void 0 ? void 0 : onClickOutside(e);
    if (closureModes.outsideClick) {
      onClose === null || onClose === void 0 ? void 0 : onClose(e, 'outsideClick');
    }
  }, [closureModes.outsideClick, onClickOutside, onClose]);
  var isClickOutsideActive = !!onClickOutside || closureModes.outsideClick;
  useCloseOnScroll(_objectSpread({
    isActive: !!closureModes.scroll,
    callback: onClose
  }, (closureOptions === null || closureOptions === void 0 ? void 0 : closureOptions.scroll) || {}));
  useCloseWhenOutsideViewport({
    tooltipRef: tooltipRef,
    isActive: closureModes.viewport,
    callback: onClose
  });
  return /*#__PURE__*/React.createElement(Popover, _extends({}, otherProps, {
    anchorRef: anchorRef || {
      current: null
    },
    animate: "animate",
    className: getClassName(invert, className),
    container: container,
    "data-tooltip-direction": originalDirection,
    exit: "exit",
    initial: "initial",
    motion: true,
    onChangeDirection: onChangeDirection,
    onClickOutside: isClickOutsideActive ? handleClickOutside : undefined,
    ref: combineRefs([ref, tooltipRef]),
    role: "tooltip",
    variants: variants
  }, getOffset(direction, anchorSize), {
    "data-t-id": "components-Tooltip"
  }), function (popoverDirection) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      align: "left",
      className: cnTooltip('content'),
      color: invert ? 'black' : 'primary-fake-invert',
      variant: "caption-medium"
    }, typeof children === 'function' ? children(convertPopoverDirection(popoverDirection)) : children), additionalContent, /*#__PURE__*/React.createElement(TooltipArrow, {
      className: cnTooltip('arrow'),
      direction: convertPopoverDirection(direction)
    }));
  });
});
var TooltipRender = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var isOpen = _ref.isOpen,
    otherProps = _objectWithoutProperties(_ref, _excluded2);
  return /*#__PURE__*/React.createElement(AnimatePresence, {
    mode: "wait"
  }, isOpen && /*#__PURE__*/React.createElement(Content, _extends({
    ref: ref
  }, otherProps)));
});
export var Tooltip = withDataConverter(dataConverter)(TooltipRender);